import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PawPrint } from "lucide-react";

const AUTH_BG = "https://images.pexels.com/photos/7474353/pexels-photo-7474353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // login fields
  const [li, setLi] = useState({ email: "", password: "" });
  // register fields
  const [rg, setRg] = useState({ name: "", email: "", password: "", phone: "" });

  async function onLogin(e) {
    e.preventDefault();
    setBusy(true);
    const res = await login(li.email, li.password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      navigate(res.user.role === "admin" ? "/admin" : "/app");
    } else {
      toast.error(res.error);
    }
  }

  async function onRegister(e) {
    e.preventDefault();
    setBusy(true);
    const res = await register(rg);
    setBusy(false);
    if (res.ok) {
      toast.success("Account created! Welcome to the pack.");
      navigate("/app");
    } else {
      toast.error(res.error);
    }
  }

  async function submitForgot(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-sand">
      <div className="relative hidden md:block">
        <img src={AUTH_BG} alt="Volunteer with dog" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-fern/70 via-fern/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <PawPrint className="w-7 h-7" strokeWidth={1.5} />
            <span className="font-heading text-xl font-semibold tracking-tight">Shelter Focus</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-bold leading-tight mb-2">Every shift saves a life.</h2>
            <p className="text-white/85 max-w-md">
              Track your volunteer hours, log dog walks, and stay connected with the team — all in one calm, simple place.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex md:hidden items-center gap-2 mb-8">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Satchel&apos;s Last Resort</span>
          </div>
          <p className="label-caps mb-2">Volunteer portal</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">
            Welcome to the<br />
            <span className="text-fern">shelter family.</span>
          </h1>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6 bg-muted">
              <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={onLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="li-email">Email</Label>
                  <Input id="li-email" data-testid="login-email" type="email" required
                    value={li.email} onChange={(e) => setLi({ ...li, email: e.target.value })}
                    placeholder="you@example.com" className="rounded-xl h-12" />
                </div>
                <div>
                  <Label htmlFor="li-pw">Password</Label>
                  <Input id="li-pw" data-testid="login-password" type="password" required
                    value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })}
                    placeholder="••••••••" className="rounded-xl h-12" />
                </div>
                <Button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full h-12 rounded-full bg-fern hover:bg-fern-dark text-white font-medium">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
                <div className="flex justify-center pt-1">
                  <button type="button" onClick={() => setForgotOpen(true)} data-testid="forgot-password"
                    className="text-xs text-muted-foreground hover:text-fern underline underline-offset-4">
                    Forgot your password?
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={onRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="rg-name">Full name</Label>
                  <Input id="rg-name" data-testid="register-name" required
                    value={rg.name} onChange={(e) => setRg({ ...rg, name: e.target.value })}
                    className="rounded-xl h-12" />
                </div>
                <div>
                  <Label htmlFor="rg-email">Email</Label>
                  <Input id="rg-email" data-testid="register-email" type="email" required
                    value={rg.email} onChange={(e) => setRg({ ...rg, email: e.target.value })}
                    className="rounded-xl h-12" />
                </div>
                <div>
                  <Label htmlFor="rg-phone">Phone (optional)</Label>
                  <Input id="rg-phone" data-testid="register-phone"
                    value={rg.phone} onChange={(e) => setRg({ ...rg, phone: e.target.value })}
                    className="rounded-xl h-12" />
                </div>
                <div>
                  <Label htmlFor="rg-pw">Password (min 6 chars)</Label>
                  <Input id="rg-pw" data-testid="register-password" type="password" required minLength={6}
                    value={rg.password} onChange={(e) => setRg({ ...rg, password: e.target.value })}
                    className="rounded-xl h-12" />
                </div>
                <Button type="submit" disabled={busy} data-testid="register-submit"
                  className="w-full h-12 rounded-full bg-terracotta hover:bg-terracotta-dark text-white font-medium">
                  {busy ? "Creating…" : "Create my volunteer account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <p className="text-xs text-muted-foreground text-center mt-8">
            By continuing you agree to our{" "}
            <Link to="/terms" className="underline hover:text-ink" data-testid="auth-terms-link">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-ink" data-testid="auth-privacy-link">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) { setForgotSent(false); setForgotEmail(""); } }}>
        <DialogContent data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset your password</DialogTitle>
            <DialogDescription>
              Enter the email address you use with Satchel&apos;s. We&apos;ll email you a secure link to set a new password.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="bg-accent/40 rounded-xl p-4 text-sm space-y-2">
              <p className="font-medium">Check your email.</p>
              <p className="text-muted-foreground">If an account exists for that address, a password reset link is on its way. The link expires in 1 hour.</p>
              <p className="text-xs text-muted-foreground">Don&apos;t see it? Check your spam folder or try again in a few minutes.</p>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" data-testid="forgot-email-input" type="email" required
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com" className="rounded-xl h-11" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} className="rounded-full">Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="forgot-submit"
                  className="rounded-full bg-fern hover:bg-fern-dark text-white">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
